import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import fieldConfigService from "./fieldConfigService";

/**
 * GET FIELD CONFIGS
 */
export const getFieldConfigs = createAsyncThunk(
  "fieldConfigs/getAll",
  async (params = {}, thunkAPI) => {
    try {
      return await fieldConfigService.getFieldConfigs(params);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/**
 * GET FIELD CONFIG BY ID
 */
export const getFieldConfigById = createAsyncThunk(
  "fieldConfigs/getById",
  async (id, thunkAPI) => {
    try {
      return await fieldConfigService.getFieldConfigById(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/**
 * GET RENDER CONFIG BY SCHOOL YEAR
 */
export const getRenderConfigBySchoolYear = createAsyncThunk(
  "fieldConfigs/getRenderConfigBySchoolYear",
  async (schoolYearId, thunkAPI) => {
    try {
      return await fieldConfigService.getRenderConfigBySchoolYear(schoolYearId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/**
 * CREATE FIELD CONFIG
 */
export const createFieldConfig = createAsyncThunk(
  "fieldConfigs/create",
  async (data, thunkAPI) => {
    try {
      return await fieldConfigService.createFieldConfig(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/**
 * UPDATE FIELD CONFIG
 */
export const updateFieldConfig = createAsyncThunk(
  "fieldConfigs/update",
  async ({ id, data }, thunkAPI) => {
    try {
      return await fieldConfigService.updateFieldConfig({ id, data });
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/**
 * DELETE FIELD CONFIG
 */
export const deleteFieldConfig = createAsyncThunk(
  "fieldConfigs/delete",
  async (id, thunkAPI) => {
    try {
      return await fieldConfigService.deleteFieldConfig(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/**
 * TOGGLE ACTIVE
 */
export const toggleFieldConfigActive = createAsyncThunk(
  "fieldConfigs/toggleActive",
  async (id, thunkAPI) => {
    try {
      return await fieldConfigService.toggleFieldConfigActive(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/**
 * REORDER FIELD CONFIGS
 */
export const reorderFieldConfigs = createAsyncThunk(
  "fieldConfigs/reorder",
  async (items, thunkAPI) => {
    try {
      return await fieldConfigService.reorderFieldConfigs(items);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/**
 * DUPLICATE FIELD CONFIGS
 */
export const duplicateFieldConfigs = createAsyncThunk(
  "fieldConfigs/duplicate",
  async ({ source_school_year, target_school_year }, thunkAPI) => {
    try {
      return await fieldConfigService.duplicateFieldConfigs({
        source_school_year,
        target_school_year,
      });
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

const initialState = {
  fieldConfigs: [],
  renderSections: [],

  selectedFieldConfig: null,

  filters: {
    schoolYear: "",
    section: "",
    active: "",
  },

  isLoading: false,
  isRenderLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isReordering: false,
  isDuplicating: false,

  error: null,
};

const upsertById = (items, updatedItem) => {
  if (!updatedItem?._id) return items;

  const exists = items.some((item) => item._id === updatedItem._id);

  if (!exists) {
    return [updatedItem, ...items];
  }

  return items.map((item) =>
    item._id === updatedItem._id ? updatedItem : item,
  );
};

const fieldConfigSlice = createSlice({
  name: "fieldConfigs",

  initialState,

  reducers: {
    clearFieldConfigError: (state) => {
      state.error = null;
    },

    setSelectedFieldConfig: (state, action) => {
      state.selectedFieldConfig = action.payload;
    },

    clearSelectedFieldConfig: (state) => {
      state.selectedFieldConfig = null;
    },

    setFieldConfigFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },

    resetFieldConfigFilters: (state) => {
      state.filters = initialState.filters;
    },

    clearRenderSections: (state) => {
      state.renderSections = [];
    },
  },

  extraReducers: (builder) => {
    builder

      /**
       * GET ALL
       */
      .addCase(getFieldConfigs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })

      .addCase(getFieldConfigs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.fieldConfigs = action.payload?.data || [];
      })

      .addCase(getFieldConfigs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Veldconfiguraties laden mislukt.";
      })

      /**
       * GET BY ID
       */
      .addCase(getFieldConfigById.fulfilled, (state, action) => {
        state.selectedFieldConfig = action.payload?.data || null;
      })

      /**
       * GET RENDER CONFIG
       */
      .addCase(getRenderConfigBySchoolYear.pending, (state) => {
        state.isRenderLoading = true;
        state.error = null;
      })

      .addCase(getRenderConfigBySchoolYear.fulfilled, (state, action) => {
        state.isRenderLoading = false;

        const data = action.payload?.data || action.payload || {};

        state.renderSections = data.sections || [];
      })

      .addCase(getRenderConfigBySchoolYear.rejected, (state, action) => {
        state.isRenderLoading = false;
        state.renderSections = [];
        state.error = action.payload || "Renderconfiguratie laden mislukt.";
      })

      /**
       * CREATE
       */
      .addCase(createFieldConfig.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })

      .addCase(createFieldConfig.fulfilled, (state, action) => {
        state.isCreating = false;

        const created = action.payload?.data;

        if (created) {
          state.fieldConfigs = upsertById(state.fieldConfigs, created);
        }
      })

      .addCase(createFieldConfig.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload || "Veldconfiguratie aanmaken mislukt.";
      })

      /**
       * UPDATE
       */
      .addCase(updateFieldConfig.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })

      .addCase(updateFieldConfig.fulfilled, (state, action) => {
        state.isUpdating = false;

        const updated = action.payload?.data;

        if (updated) {
          state.fieldConfigs = upsertById(state.fieldConfigs, updated);

          if (state.selectedFieldConfig?._id === updated._id) {
            state.selectedFieldConfig = updated;
          }
        }
      })

      .addCase(updateFieldConfig.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload || "Veldconfiguratie wijzigen mislukt.";
      })

      /**
       * TOGGLE ACTIVE
       */
      .addCase(toggleFieldConfigActive.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })

      .addCase(toggleFieldConfigActive.fulfilled, (state, action) => {
        state.isUpdating = false;

        const updated = action.payload?.data;

        if (updated) {
          state.fieldConfigs = upsertById(state.fieldConfigs, updated);
        }
      })

      .addCase(toggleFieldConfigActive.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload || "Status wijzigen mislukt.";
      })

      /**
       * DELETE
       */
      .addCase(deleteFieldConfig.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })

      .addCase(deleteFieldConfig.fulfilled, (state, action) => {
        state.isDeleting = false;

        const deletedId = action.meta.arg;

        state.fieldConfigs = state.fieldConfigs.filter(
          (item) => item._id !== deletedId,
        );

        if (state.selectedFieldConfig?._id === deletedId) {
          state.selectedFieldConfig = null;
        }
      })

      .addCase(deleteFieldConfig.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload || "Veldconfiguratie verwijderen mislukt.";
      })

      /**
       * REORDER
       */
      .addCase(reorderFieldConfigs.pending, (state) => {
        state.isReordering = true;
        state.error = null;
      })

      .addCase(reorderFieldConfigs.fulfilled, (state, action) => {
        state.isReordering = false;

        const updatedItems = action.payload?.data;

        if (Array.isArray(updatedItems)) {
          state.fieldConfigs = updatedItems;
        }
      })

      .addCase(reorderFieldConfigs.rejected, (state, action) => {
        state.isReordering = false;
        state.error = action.payload || "Volgorde wijzigen mislukt.";
      })

      /**
       * DUPLICATE
       */
      .addCase(duplicateFieldConfigs.pending, (state) => {
        state.isDuplicating = true;
        state.error = null;
      })

      .addCase(duplicateFieldConfigs.fulfilled, (state) => {
        state.isDuplicating = false;
      })

      .addCase(duplicateFieldConfigs.rejected, (state, action) => {
        state.isDuplicating = false;
        state.error = action.payload || "Veldconfiguraties dupliceren mislukt.";
      });
  },
});

export const {
  clearFieldConfigError,
  setSelectedFieldConfig,
  clearSelectedFieldConfig,
  setFieldConfigFilters,
  resetFieldConfigFilters,
  clearRenderSections,
} = fieldConfigSlice.actions;

export default fieldConfigSlice.reducer;
